import { useEffect, useCallback } from "react";
import type { RouteId } from "../lib/routes";

export interface ShortcutAction {
  key: string;
  meta?: boolean;
  shift?: boolean;
  alt?: boolean;
  label: string;
  description: string;
  category: "navigation" | "actions" | "views" | "general";
  action: () => void;
}

function isInputFocused(): boolean {
  const tag = (document.activeElement as HTMLElement)?.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

interface UseShortcutsOptions {
  navigate: (route: RouteId) => void;
  toggleSidebar: () => void;
  openCommandPalette: () => void;
  closeSheet: () => void;
  focusSearch: () => void;
  openNewTask?: () => void;
}

export function useShortcuts({
  navigate,
  toggleSidebar,
  openCommandPalette,
  closeSheet,
  focusSearch,
  openNewTask,
}: UseShortcutsOptions) {

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const meta = e.metaKey || e.ctrlKey;

    // ── Command Palette: ⌘K ─────────────────────────────────
    if (meta && e.key === "k") {
      e.preventDefault();
      openCommandPalette();
      return;
    }

    // ── Escape: close overlays ──────────────────────────────
    if (e.key === "Escape") {
      e.preventDefault();
      closeSheet();
      return;
    }

    // Don't process shortcuts when typing in inputs
    if (isInputFocused()) return;

    // ── N key — new task ────────────────────────────────────
    if (e.key === "n" && !meta && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      openNewTask?.();
      return;
    }

    // ── Navigation: 1-0 for pages ───────────────────────────
    const routeMap: Record<string, RouteId> = {
      "1": "dashboard",
      "2": "board",
      "3": "path",
      "4": "timeline",
      "5": "pipeline",
      "6": "channels",
      "7": "contacts",
      "8": "scorecard",
      "9": "checklist",
      "0": "assets",
    };

    const mappedRoute = routeMap[e.key];
    if (!meta && !e.shiftKey && !e.altKey && mappedRoute) {
      e.preventDefault();
      navigate(mappedRoute);
      return;
    }

    // ── g then letter: go-to shortcuts (vim-style) ──────────
    // Handled via two-key combo tracked below

    // ── Search: / to focus search ───────────────────────────
    if (e.key === "/" && !meta) {
      e.preventDefault();
      focusSearch();
      return;
    }

    // ── Toggle sidebar: [ ───────────────────────────────────
    if (e.key === "[" && !meta) {
      e.preventDefault();
      toggleSidebar();
      return;
    }

    // ── ⌘B: Toggle sidebar ─────────────────────────────────
    if (meta && e.key === "b") {
      e.preventDefault();
      toggleSidebar();
      return;
    }

  }, [navigate, toggleSidebar, openCommandPalette, closeSheet, focusSearch]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);
}

// ── Shortcut registry for display in command palette / help ──

export function getShortcutsList(): Array<{
  keys: string;
  label: string;
  category: "navigation" | "actions" | "views" | "general";
}> {
  return [
    // General
    { keys: "⌘ K", label: "Open command palette", category: "general" },
    { keys: "Esc", label: "Close overlay / sheet", category: "general" },
    { keys: "/", label: "Focus search", category: "general" },
    { keys: "?", label: "Show keyboard shortcuts", category: "general" },
    { keys: "N", label: "Create new task", category: "actions" },

    // Navigation
    { keys: "1", label: "Go to Command Center", category: "navigation" },
    { keys: "2", label: "Work Board → Kanban", category: "navigation" },
    { keys: "3", label: "Work Board → Runbook", category: "navigation" },
    { keys: "4", label: "Work Board → Timeline", category: "navigation" },
    { keys: "5", label: "Work Board → Pipeline", category: "navigation" },
    { keys: "6", label: "Go to Distribution Ops", category: "navigation" },
    { keys: "7", label: "Go to Contact Ops", category: "navigation" },
    { keys: "8", label: "Go to Revenue Scorecard", category: "navigation" },
    { keys: "9", label: "Work Board → Checklist", category: "navigation" },
    { keys: "0", label: "Go to Assets", category: "navigation" },

    // Views
    { keys: "[", label: "Toggle sidebar", category: "views" },
    { keys: "⌘ B", label: "Toggle sidebar", category: "views" },
  ];
}
